/**
 * The wording of API failures, in the reader's language.
 *
 * `lib/api-errors.ts` composes a message from a field label, a validation line and a "Solution: …"
 * hint. All of that copy was hardcoded French, so the one part of the app that explains what went
 * wrong was unreadable to the Arabic/Darija users it is being built for — on the very screens (agent
 * package forms, resident requests) where someone is most likely to be stuck.
 *
 * The French text stays as the code's fallback for a field it does not know, so an unfamiliar key can
 * never surface as a raw `apiErrors.hints.xyz`.
 */
export const apiErrorMessages = {
  fr: {
    solutionPrefix: 'À faire :',
    fallback: 'Ça n’a pas marché. Vérifiez les informations, puis réessayez.',
    serverError: 'Un problème est survenu de notre côté. Réessayez dans un instant, ou contactez-nous.',
    fields: {
      agent_id: 'Agent',
      recipient_code: 'le code du destinataire',
      delivery_mode: 'le mode de livraison',
      package_size: 'la taille du colis',
      size: 'la taille',
      cash_collected_centimes: 'Argent encaissé',
      cod_amount_centimes: 'Montant à encaisser',
      cod_amount_mad: 'Montant à encaisser',
      credit_balance_centimes: 'Crédit du partenaire',
      current_password: 'Mot de passe actuel',
      delivery_ids: 'Colis choisis',
      email: 'E-mail',
      exception_code: 'Raison du problème',
      handover_code: 'Code de remise',
      handover_codes: 'Codes de remise',
      identity_doc: 'Carte d’identité',
      idempotency_key: 'Demande',
      internal_ref: 'Référence interne',
      operator_tracking_ref: 'Référence de suivi',
      password: 'Mot de passe',
      partner_id: 'Partenaire',
      phone: 'Téléphone',
      photo: 'Photo',
      photos: 'Photos',
      proof_of_residence: 'Justificatif de domicile',
      reason_text: 'Note',
      recipient_address: 'Adresse du destinataire',
      recipient_name: 'Nom du destinataire',
      recipient_phone: 'Téléphone du destinataire',
      residence_id: 'Point',
      signature: 'Signature',
      action: 'l’action',
      amount_centimes: 'le montant',
      cod_amount: 'le montant à encaisser',
      lat: 'la position',
      lng: 'la position',
      livreur_id: 'le livreur',
      method: 'la méthode',
      note: 'la note',
      notes: 'les notes',
      package_code: 'le code du colis',
      photo_storage_key: 'la photo',
      pickup_address: 'l’adresse de collecte',
      sender_name: 'le nom de l’expéditeur',
      sender_phone: 'le téléphone de l’expéditeur',
      sender_address: 'l’adresse de collecte',
      sender_geo_lat: 'la position de collecte',
      sender_geo_lng: 'la position de collecte',
      collection_mode: 'le mode de collecte',
      pickup_agent_id: 'l’agent de dépôt',
      pickup_residence_id: 'le point de dépôt',
      pod_type: 'le type de preuve',
      radius_km: 'la distance en km',
      reason: 'la raison',
      requested_note: 'la note',
      requester_address: 'l’adresse du demandeur',
      requester_name: 'le nom du demandeur',
      requester_phone: 'le téléphone du demandeur',
      signature_storage_key: 'la signature',
      type: 'le type',
      geo_lat: 'la position',
      geo_lng: 'la position',
      manifest: 'le fichier',
    },
    hints: {
      agent_id: 'Choisissez un agent actif du même point, ou laissez vide pour qu’il soit choisi automatiquement.',
      cod_amount_mad: 'Entrez le montant en dirhams, par exemple 125,50. Mettez 0 s’il n’y a rien à encaisser.',
      credit_balance_centimes: 'Ajoutez du crédit au partenaire avant de créer ou de régler ce colis.',
      internal_ref: 'Laissez vide pour créer la référence automatiquement, ou écrivez une valeur unique.',
      operator_tracking_ref: 'Écrivez une référence unique. Dans le formulaire manuel, laissez vide pour la créer automatiquement.',
      partner_id: 'Choisissez un partenaire actif avec assez de crédit, ou ajoutez du crédit avant de continuer.',
      recipient_address: 'Écrivez l’adresse complète du destinataire.',
      recipient_name: 'Écrivez le nom complet du destinataire.',
      recipient_phone: 'Écrivez le numéro avec l’indicatif du pays, par exemple +212 6 00 00 00 00.',
      residence_id: 'Écrivez le nom du point exactement comme dans la liste sous le fichier, ou téléchargez le modèle CSV.',
      csvRow: 'Corrigez cette ligne dans le fichier CSV, puis renvoyez seulement les lignes refusées.'
    },
    codes: {
      INSUFFICIENT_WALLET_BALANCE: {
        message: 'La caution de cet agent est insuffisante.',
        hint: 'Rechargez la caution de l’agent, ou donnez le colis à un agent qui a assez de solde.'
      },
      CUSTOMER_CREDIT_EXHAUSTED: {
        message: 'Votre crédit est épuisé.',
        hint: 'Ajoutez du crédit, puis réessayez.'
      },
      INVALID_STATE_TRANSITION: {
        message: 'Cette action n’est pas possible à cette étape du colis.',
        hint: 'Regardez où en est le colis et utilisez seulement les boutons proposés.'
      },
      POD_INCOMPLETE: {
        message: 'Il manque des informations pour la preuve de livraison.',
        hint: 'Ajoutez les photos, la signature, votre position ou l’argent encaissé demandés.'
      },
      IDEMPOTENCY_KEY_REUSED: {
        message: 'Cette action a déjà été faite.',
        hint: 'Refaites l’action depuis l’écran.'
      },
      SENDER_PIN_RESEND_RATE_LIMITED: {
        message: 'Le code a été renvoyé trop de fois.',
        hint: 'Attendez une heure avant de le renvoyer.'
      },
      UNAUTHENTICATED: {
        message: 'Votre session est terminée. Connectez-vous de nouveau.',
        hint: ''
      },
      FORBIDDEN: {
        message: 'Vous n’avez pas le droit de faire cela.',
        hint: ''
      },
      RESOURCE_NOT_FOUND: {
        message: 'Introuvable.',
        hint: 'Vérifiez la référence, ou rechargez la page.'
      },
      CONFLICT: {
        message: 'Cette action gêne une autre action en cours.',
        hint: ''
      },
      VALIDATION_ERROR: {
        message: 'Des informations sont incorrectes.',
        hint: ''
      },
      RATE_LIMIT_EXCEEDED: {
        message: 'Trop d’essais. Attendez un instant.',
        hint: ''
      },
      INTERNAL_ERROR: {
        message: 'Un problème est survenu de notre côté.',
        hint: 'Réessayez dans un instant, ou contactez-nous.'
      },
      BAD_REQUEST: {
        message: 'La demande n’est pas valable.',
        hint: ''
      }

    }
  },
  en: {
    solutionPrefix: 'What to do:',
    fallback: 'That did not work. Check the information, then try again.',
    serverError: 'Something went wrong on our side. Try again in a moment, or contact us.',
    fields: {
      agent_id: 'Agent',
      recipient_code: 'the recipient’s code',
      delivery_mode: 'the delivery mode',
      package_size: 'the parcel size',
      size: 'the size',
      cash_collected_centimes: 'Money collected',
      cod_amount_centimes: 'Amount to collect',
      cod_amount_mad: 'Amount to collect',
      credit_balance_centimes: 'Partner’s credit',
      current_password: 'Current password',
      delivery_ids: 'Chosen parcels',
      email: 'Email',
      exception_code: 'Reason for the problem',
      handover_code: 'Hand-over code',
      handover_codes: 'Hand-over codes',
      identity_doc: 'ID card',
      idempotency_key: 'Request',
      internal_ref: 'Internal reference',
      operator_tracking_ref: 'Tracking reference',
      password: 'Password',
      partner_id: 'Partner',
      phone: 'Phone',
      photo: 'Photo',
      photos: 'Photos',
      proof_of_residence: 'Proof of address',
      reason_text: 'Note',
      recipient_address: 'Recipient’s address',
      recipient_name: 'Recipient’s name',
      recipient_phone: 'Recipient’s phone',
      residence_id: 'Point',
      signature: 'Signature',
      action: 'the action',
      amount_centimes: 'the amount',
      cod_amount: 'the amount to collect',
      lat: 'the location',
      lng: 'the location',
      livreur_id: 'the courier',
      method: 'the method',
      note: 'the note',
      notes: 'the notes',
      package_code: 'the parcel code',
      photo_storage_key: 'the photo',
      pickup_address: 'the pickup address',
      sender_name: 'the sender’s name',
      sender_phone: 'the sender’s phone',
      sender_address: 'the pickup address',
      sender_geo_lat: 'the pickup location',
      sender_geo_lng: 'the pickup location',
      collection_mode: 'how it is collected',
      pickup_agent_id: 'the drop-off agent',
      pickup_residence_id: 'the drop-off point',
      pod_type: 'the type of proof',
      radius_km: 'the distance in km',
      reason: 'the reason',
      requested_note: 'the note',
      requester_address: 'the requester’s address',
      requester_name: 'the requester’s name',
      requester_phone: 'the requester’s phone',
      signature_storage_key: 'the signature',
      type: 'the type',
      geo_lat: 'the location',
      geo_lng: 'the location',
      manifest: 'the file',
    },
    hints: {
      agent_id: 'Choose an active agent from the same point, or leave empty to have one chosen automatically.',
      cod_amount_mad: 'Enter the amount in dirhams, for example 125.50. Put 0 if there is nothing to collect.',
      credit_balance_centimes: 'Add credit for the partner before creating or settling this parcel.',
      internal_ref: 'Leave empty to create the reference automatically, or write a unique value.',
      operator_tracking_ref: 'Write a unique reference. In the manual form, leave empty to create it automatically.',
      partner_id: 'Choose an active partner with enough credit, or add credit before going on.',
      recipient_address: 'Write the recipient’s full address.',
      recipient_name: 'Write the recipient’s full name.',
      recipient_phone: 'Write the number with the country code, for example +212 6 00 00 00 00.',
      residence_id: 'Write the point\'s name exactly as in the list under the file, or download the CSV template.',
      csvRow: 'Fix this line in the CSV file, then send only the refused lines again.'
    },
    codes: {
      INSUFFICIENT_WALLET_BALANCE: {
        message: 'This agent’s deposit is too low.',
        hint: 'Top up the agent’s deposit, or give the parcel to an agent with enough balance.'
      },
      CUSTOMER_CREDIT_EXHAUSTED: {
        message: 'Your credit has run out.',
        hint: 'Add credit, then try again.'
      },
      INVALID_STATE_TRANSITION: {
        message: 'This action is not possible at this step of the parcel.',
        hint: 'Look at where the parcel is and use only the buttons shown.'
      },
      POD_INCOMPLETE: {
        message: 'Some information is missing for the proof of delivery.',
        hint: 'Add the photos, signature, your location or the money collected that are asked for.'
      },
      IDEMPOTENCY_KEY_REUSED: {
        message: 'This action was already done.',
        hint: 'Do the action again from the screen.'
      },
      SENDER_PIN_RESEND_RATE_LIMITED: {
        message: 'The code was sent again too many times.',
        hint: 'Wait an hour before sending it again.'
      },
      UNAUTHENTICATED: {
        message: 'Your session ended. Log in again.',
        hint: ''
      },
      FORBIDDEN: {
        message: 'You are not allowed to do this.',
        hint: ''
      },
      RESOURCE_NOT_FOUND: {
        message: 'Not found.',
        hint: 'Check the reference, or reload the page.'
      },
      CONFLICT: {
        message: 'This action clashes with another one in progress.',
        hint: ''
      },
      VALIDATION_ERROR: {
        message: 'Some information is wrong.',
        hint: ''
      },
      RATE_LIMIT_EXCEEDED: {
        message: 'Too many tries. Wait a moment.',
        hint: ''
      },
      INTERNAL_ERROR: {
        message: 'Something went wrong on our side.',
        hint: 'Try again in a moment, or contact us.'
      },
      BAD_REQUEST: {
        message: 'The request is not valid.',
        hint: ''
      }

    }
  },
  ar: {
    solutionPrefix: 'المطلوب:',
    fallback: 'لم ينجح الأمر. تحقق من المعلومات ثم حاول مرة أخرى.',
    serverError: 'حدث خلل من جهتنا. حاول بعد قليل أو تواصل معنا.',
    fields: {
      agent_id: 'الوكيل',
      recipient_code: 'رمز المستلم',
      delivery_mode: 'طريقة التسليم',
      package_size: 'حجم الطرد',
      size: 'الحجم',
      cash_collected_centimes: 'المبلغ المحصّل',
      cod_amount_centimes: 'المبلغ المطلوب تحصيله',
      cod_amount_mad: 'المبلغ المطلوب تحصيله',
      credit_balance_centimes: 'رصيد الشريك',
      current_password: 'كلمة المرور الحالية',
      delivery_ids: 'الطرود المختارة',
      email: 'البريد الإلكتروني',
      exception_code: 'سبب المشكل',
      handover_code: 'رمز التسليم',
      handover_codes: 'رموز التسليم',
      identity_doc: 'بطاقة الهوية',
      idempotency_key: 'الطلب',
      internal_ref: 'المرجع الداخلي',
      operator_tracking_ref: 'رقم التتبع',
      password: 'كلمة المرور',
      partner_id: 'الشريك',
      phone: 'الهاتف',
      photo: 'الصورة',
      photos: 'الصور',
      proof_of_residence: 'إثبات السكن',
      reason_text: 'ملاحظة',
      recipient_address: 'عنوان المستلم',
      recipient_name: 'اسم المستلم',
      recipient_phone: 'هاتف المستلم',
      residence_id: 'النقطة',
      signature: 'التوقيع',
      action: 'الإجراء',
      amount_centimes: 'المبلغ',
      cod_amount: 'المبلغ المطلوب تحصيله',
      lat: 'الموقع',
      lng: 'الموقع',
      livreur_id: 'الموزّع',
      method: 'الطريقة',
      note: 'الملاحظة',
      notes: 'الملاحظات',
      package_code: 'رمز الطرد',
      photo_storage_key: 'الصورة',
      pickup_address: 'عنوان الجمع',
      sender_name: 'اسم المرسل',
      sender_phone: 'هاتف المرسل',
      sender_address: 'عنوان الجمع',
      sender_geo_lat: 'موقع الجمع',
      sender_geo_lng: 'موقع الجمع',
      collection_mode: 'طريقة الجمع',
      pickup_agent_id: 'وكيل الإيداع',
      pickup_residence_id: 'نقطة الإيداع',
      pod_type: 'نوع الإثبات',
      radius_km: 'المسافة بالكيلومتر',
      reason: 'السبب',
      requested_note: 'الملاحظة',
      requester_address: 'عنوان صاحب الطلب',
      requester_name: 'اسم صاحب الطلب',
      requester_phone: 'هاتف صاحب الطلب',
      signature_storage_key: 'التوقيع',
      type: 'النوع',
      geo_lat: 'الموقع',
      geo_lng: 'الموقع',
      manifest: 'الملف',
    },
    hints: {
      agent_id: 'اختر وكيلا نشطا من نفس النقطة أو اتركه فارغا ليُختار تلقائيا.',
      cod_amount_mad: 'أدخل المبلغ بالدرهم، مثل 125.50. ضع 0 إن لم يكن هناك مبلغ للتحصيل.',
      credit_balance_centimes: 'أضف رصيدا للشريك قبل إنشاء هذا الطرد أو تسويته.',
      internal_ref: 'اتركه فارغا لإنشاء المرجع تلقائيا أو اكتب قيمة غير مكررة.',
      operator_tracking_ref: 'اكتب مرجعا غير مكرر. في الاستمارة اليدوية اتركه فارغا لإنشائه تلقائيا.',
      partner_id: 'اختر شريكا نشطا برصيد كاف أو أضف رصيدا قبل المتابعة.',
      recipient_address: 'اكتب العنوان الكامل للمستلم.',
      recipient_name: 'اكتب الاسم الكامل للمستلم.',
      recipient_phone: 'اكتب الرقم مع رمز البلد، مثل ‎+212 6 00 00 00 00.',
      residence_id: 'اكتب اسم النقطة كما هو في القائمة أسفل الملف أو حمّل نموذج CSV.',
      csvRow: 'صحح هذا السطر في ملف CSV ثم أعد إرسال الأسطر المرفوضة فقط.'
    },
    codes: {
      INSUFFICIENT_WALLET_BALANCE: {
        message: 'ضمان هذا الوكيل غير كاف.',
        hint: 'اشحن ضمان الوكيل أو أسند الطرد إلى وكيل رصيده كاف.'
      },
      CUSTOMER_CREDIT_EXHAUSTED: {
        message: 'نفد رصيدك.',
        hint: 'أضف رصيدا ثم حاول مرة أخرى.'
      },
      INVALID_STATE_TRANSITION: {
        message: 'هذا الإجراء غير ممكن في هذه المرحلة من الطرد.',
        hint: 'انظر أين وصل الطرد واستعمل الأزرار المعروضة فقط.'
      },
      POD_INCOMPLETE: {
        message: 'هناك معلومات ناقصة في إثبات التسليم.',
        hint: 'أضف الصور والتوقيع وموقعك أو المبلغ المحصّل المطلوب.'
      },
      IDEMPOTENCY_KEY_REUSED: {
        message: 'تم هذا الإجراء من قبل.',
        hint: 'أعد الإجراء من الشاشة.'
      },
      SENDER_PIN_RESEND_RATE_LIMITED: {
        message: 'أُعيد إرسال الرمز مرات كثيرة.',
        hint: 'انتظر ساعة قبل إعادة الإرسال.'
      },
      UNAUTHENTICATED: {
        message: 'انتهت جلستك. سجّل الدخول من جديد.',
        hint: ''
      },
      FORBIDDEN: {
        message: 'لا تملك صلاحية القيام بهذا.',
        hint: ''
      },
      RESOURCE_NOT_FOUND: {
        message: 'غير موجود.',
        hint: 'تحقق من المرجع أو أعد تحميل الصفحة.'
      },
      CONFLICT: {
        message: 'هذا الإجراء يتعارض مع إجراء آخر جارٍ.',
        hint: ''
      },
      VALIDATION_ERROR: {
        message: 'بعض المعلومات غير صحيحة.',
        hint: ''
      },
      RATE_LIMIT_EXCEEDED: {
        message: 'محاولات كثيرة. انتظر قليلا.',
        hint: ''
      },
      INTERNAL_ERROR: {
        message: 'حدث خلل من جهتنا.',
        hint: 'حاول بعد قليل أو تواصل معنا.'
      },
      BAD_REQUEST: {
        message: 'الطلب غير صالح.',
        hint: ''
      }

    }
  },
  ma: {
    solutionPrefix: 'شنو تدير:',
    fallback: 'ما نجحاتش. شوف المعلومات ومن بعد عاود جرب.',
    serverError: 'وقعات مشكلة عندنا. عاود جرب من بعد شوية ولا تاصل بينا.',
    fields: {
      agent_id: 'الكارديان',
      recipient_code: 'كود المستلم',
      delivery_mode: 'طريقة التسليم',
      package_size: 'حجم الكولي',
      size: 'الحجم',
      cash_collected_centimes: 'الفلوس اللي خديتي',
      cod_amount_centimes: 'المبلغ اللي خاص يتخلص',
      cod_amount_mad: 'المبلغ اللي خاص يتخلص',
      credit_balance_centimes: 'الكريدي ديال الشريك',
      current_password: 'الكلمة السرية الحالية',
      delivery_ids: 'الكوليات اللي ختاريتي',
      email: 'الإيميل',
      exception_code: 'سبب المشكل',
      handover_code: 'كود التسليم',
      handover_codes: 'أكواد التسليم',
      identity_doc: 'بطاقة التعريف',
      idempotency_key: 'الطلب',
      internal_ref: 'المرجع الداخلي',
      operator_tracking_ref: 'نمرة التتبع',
      password: 'الكلمة السرية',
      partner_id: 'الشريك',
      phone: 'التيليفون',
      photo: 'التصويرة',
      photos: 'التصاور',
      proof_of_residence: 'إثبات السكنى',
      reason_text: 'ملاحظة',
      recipient_address: 'عنوان المستلم',
      recipient_name: 'سمية المستلم',
      recipient_phone: 'تيليفون المستلم',
      residence_id: 'النقطة',
      signature: 'التوقيع',
      action: 'العملية',
      amount_centimes: 'المبلغ',
      cod_amount: 'المبلغ اللي خاص يتخلص',
      lat: 'البلاصة',
      lng: 'البلاصة',
      livreur_id: 'الليفرور',
      method: 'الطريقة',
      note: 'الملاحظة',
      notes: 'الملاحظات',
      package_code: 'كود الكولي',
      photo_storage_key: 'التصويرة',
      pickup_address: 'عنوان الجمع',
      sender_name: 'سمية المرسل',
      sender_phone: 'تيليفون المرسل',
      sender_address: 'عنوان الجمع',
      sender_geo_lat: 'بلاصة الجمع',
      sender_geo_lng: 'بلاصة الجمع',
      collection_mode: 'طريقة الجمع',
      pickup_agent_id: 'كارديان الإيداع',
      pickup_residence_id: 'نقطة الإيداع',
      pod_type: 'نوع الإثبات',
      radius_km: 'المسافة بالكيلومتر',
      reason: 'السبب',
      requested_note: 'الملاحظة',
      requester_address: 'عنوان صاحب الطلب',
      requester_name: 'سمية صاحب الطلب',
      requester_phone: 'تيليفون صاحب الطلب',
      signature_storage_key: 'التوقيع',
      type: 'النوع',
      geo_lat: 'البلاصة',
      geo_lng: 'البلاصة',
      manifest: 'الملف',
    },
    hints: {
      agent_id: 'ختار كارديان خدام من نفس النقطة، ولا خليه خاوي باش يتختار وحدو.',
      cod_amount_mad: 'دخل المبلغ بالدرهم، بحال 125.50. حط 0 إلا ماكاين والو باش يتخلص.',
      credit_balance_centimes: 'زيد الكريدي للشريك قبل ما تصاوب ولا تسوي هاد الكولي.',
      internal_ref: 'خليه خاوي باش يتصاوب المرجع وحدو، ولا كتب قيمة ما كاينش بحالها.',
      operator_tracking_ref: 'كتب مرجع ما كاينش بحالو. فالاستمارة اليدوية خليه خاوي باش يتصاوب وحدو.',
      partner_id: 'ختار شريك خدام عندو كريدي كافي، ولا زيد الكريدي قبل ما تكمل.',
      recipient_address: 'كتب العنوان الكامل ديال المستلم.',
      recipient_name: 'كتب السمية الكاملة ديال المستلم.',
      recipient_phone: 'كتب النمرة مع كود البلاد، بحال ‎+212 6 00 00 00 00.',
      residence_id: 'كتب سمية النقطة بحال ما هي فاللائحة لتحت الملف ولا نزّل نموذج CSV.',
      csvRow: 'صلح هاد السطر فملف CSV، ومن بعد عاود صيفط غير السطور اللي ترفضات.'
    },
    codes: {
      INSUFFICIENT_WALLET_BALANCE: {
        message: 'الضمانة ديال هاد الكارديان ما كافيش.',
        hint: 'زيد فالضمانة ديال الكارديان، ولا عطي الكولي لكارديان عندو رصيد كافي.'
      },
      CUSTOMER_CREDIT_EXHAUSTED: {
        message: 'الكريدي ديالك سالا.',
        hint: 'زيد الكريدي ومن بعد عاود جرب.'
      },
      INVALID_STATE_TRANSITION: {
        message: 'هاد العملية ماشي ممكنة فهاد المرحلة د الكولي.',
        hint: 'شوف فين وصل الكولي واستعمل غير البوطونات اللي باينين.'
      },
      POD_INCOMPLETE: {
        message: 'كاين معلومات ناقصين فإثبات التسليم.',
        hint: 'زيد التصاور، التوقيع، البلاصة ديالك ولا الفلوس اللي خديتي المطلوبين.'
      },
      IDEMPOTENCY_KEY_REUSED: {
        message: 'هاد العملية تدارت من قبل.',
        hint: 'عاود دير العملية من الشاشة.'
      },
      SENDER_PIN_RESEND_RATE_LIMITED: {
        message: 'الكود تعاود تصيفط بزاف د المرات.',
        hint: 'تسنى ساعة قبل ما تعاود تصيفطو.'
      },
      UNAUTHENTICATED: {
        message: 'الجلسة ديالك سالات. دخل من جديد.',
        hint: ''
      },
      FORBIDDEN: {
        message: 'ما عندكش الحق دير هادشي.',
        hint: ''
      },
      RESOURCE_NOT_FOUND: {
        message: 'ماكاينش.',
        hint: 'تأكد من المرجع ولا عاود حمل الصفحة.'
      },
      CONFLICT: {
        message: 'هاد العملية كتتعارض مع عملية أخرى خدامة.',
        hint: ''
      },
      VALIDATION_ERROR: {
        message: 'شي معلومات ماشي صحاح.',
        hint: ''
      },
      RATE_LIMIT_EXCEEDED: {
        message: 'بزاف د المحاولات. تسنى شوية.',
        hint: ''
      },
      INTERNAL_ERROR: {
        message: 'وقعات مشكلة عندنا.',
        hint: 'عاود جرب من بعد شوية ولا تاصل بينا.'
      },
      BAD_REQUEST: {
        message: 'الطلب ماشي صحيح.',
        hint: ''
      }

    }
  }
} as const
